// React Dependencies
import React, { useState, useRef, useMemo } from 'react';

// External Dependencies
import _ from 'lodash';
import MaterialTable from 'material-table';
import moment from 'moment';
import { TextField, Select, MenuItem } from '@material-ui/core';
import { toast } from 'react-toastify';
import { JobType } from '../../common/constants';

// Internal Dependencies
import apiService from '../../services.js/apiService';
import { IsEmpty } from '../../common/checks';

const JobStatus = { Running: 'Running', Error: 'Error' };

const defaultColumns = [
  { title: 'Job Name', field: 'name', filtering: true, editable: 'never' },
  {
    title:           'Last run at',
    field:           'lastRunAt',
    editable:        'never',
    filtering:       true,
    filterComponent: (props) => (
      <div className="d-flex">
        <TextField
          id="startDate"
          size="small"
          type="datetime-local"
          defaultValue=""
          label="Start Date"
          className="mr-2"
          InputLabelProps={{ shrink: true }}
          InputProps={{ style: { maxWidth: '14.5rem' } }}
          style={{ position: 'relative', top: '-0.5rem' }}
          onChange={(e) => {
            // eslint-disable-next-line react/prop-types
            const { id, filterValue: prevValue } = props.columnDef.tableData;
            const prevEnd = Array.isArray(prevValue) && !IsEmpty(prevValue) ? prevValue[1] : null;
            const newValue = [e.target.value, prevEnd];
            // eslint-disable-next-line react/prop-types
            props.onFilterChanged(id, newValue);
          }}
        />
        <TextField
          id="endDate"
          size="small"
          type="datetime-local"
          defaultValue=""
          label="End Date"
          InputLabelProps={{ shrink: true }}
          InputProps={{ style: { maxWidth: '14.5rem' } }}
          style={{ position: 'relative', top: '-0.5rem' }}
          onChange={(e) => {
            // eslint-disable-next-line react/prop-types
            const { id, filterValue: prevValue } = props.columnDef.tableData;
            const prevStart = Array.isArray(prevValue) && !IsEmpty(prevValue) ? prevValue[0] : null;
            const newValue = [prevStart, e.target.value];
            // eslint-disable-next-line react/prop-types
            props.onFilterChanged(id, newValue);
          }}
        />
      </div>
    ),
    render: (data) => (IsEmpty(data.lastRunAt) ? '' : moment(data.lastRunAt).format('DD MMMM YYYY HH:mm:ss')),
  },
  {
    title:           'Type',
    field:           'type',
    editable:        'never',
    filtering:       true,
    filterComponent: (props) => (
      <Select
        className="w-100"
        // eslint-disable-next-line react/prop-types
        onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
      >
        <MenuItem value="">-</MenuItem>
        <MenuItem value={JobType.Backend}>{JobType.Backend}</MenuItem>
        <MenuItem value={JobType.FoodReviewScraper}>{JobType.FoodReviewScraper}</MenuItem>
        <MenuItem value={JobType.MallScraper}>{JobType.MallScraper}</MenuItem>
      </Select>
    ),
  },
  {
    title:           'Status',
    field:           'status',
    editable:        'never',
    filtering:       true,
    filterComponent: (props) => (
      <Select
        className="w-100"
        // eslint-disable-next-line react/prop-types
        onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
      >
        <MenuItem value="">-</MenuItem>
        <MenuItem value="Running">Running</MenuItem>
        <MenuItem value="Error">Error</MenuItem>
      </Select>
    ),
  },
];

const SystemStatus = () => {
  const tableRef = useRef(null);
  const columns = useMemo(() => defaultColumns, []);
  const [isLoading, setIsLoading] = useState(true);

  // eslint-disable-next-line no-async-promise-executor
  const fetchTableData = (query = {}) => new Promise(async (resolve) => {
    try {
      setIsLoading(true);

      const { orderBy, orderDirection, filters } = query;
      const queryFilters = Object.fromEntries(filters.map((f) => [f.column.field, f.value]));
      let systemStatusList = await apiService.getSystemStatuses(queryFilters);

      systemStatusList = systemStatusList.map((item) => ({ ...item, status: (item.isRunning ? JobStatus.Running : JobStatus.Error) }));

      if (!IsEmpty(systemStatusList) && !IsEmpty(queryFilters.status)) {
        systemStatusList = systemStatusList.filter((item) => (queryFilters.status === JobStatus.Running ? item.isRunning : !item.isRunning));
      }

      if (!IsEmpty(orderBy) && !IsEmpty(orderDirection)) {
        systemStatusList = _.orderBy(systemStatusList, [orderBy.field], [orderDirection]);
      } else {
        systemStatusList = _.orderBy(systemStatusList, ['name'], ['asc']);
      }

      resolve({ data: systemStatusList  });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [] });
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      <div className="content-header">
        <h1>System Status</h1>
      </div>
      <MaterialTable
        tableRef={tableRef}
        isLoading={isLoading}
        title=""
        columns={columns}
        components={{ Toolbar: () => null }}
        options={{ paging: false, filtering: true, search: false, emptyRowsWhenPaging: false, debounceInterval: 500 }}
        data={fetchTableData}
      />
    </>
  );
};

export default SystemStatus;
